import { Component, DoCheck, HostBinding, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { ComponentCanDeactivate } from '../../../../interfaces/component-can-deactivate';
import { PersonEnvelope } from '../../../../models/net/envelope/person-envelope';
import { Observable, Subject, takeUntil } from 'rxjs';
import { AppService } from '../../../../services/app.service';
import { ActivatedRoute } from '@angular/router';
import { Location } from '@angular/common';
import { PersonService } from '../../../../services/persons/person.service';
import { ActionButton } from '../../../../models/app/action-button';
import { EditorComponent } from '../../../base/editor/editor.component';
import { HistoryComponent } from '../../../fragments/history/history.component';
import { MatTabChangeEvent } from '@angular/material/tabs';
import { ChipSelectorComponent } from '../../../fragments/chip-selector/chip-selector.component';
import { AvatarPrimitive } from '../../../../models/app/avatar-primitive';
import { PersonDocument } from '../../../../models/net/envelope/documents/person-document';
import { GroupPerson } from '../../../../models/data/app-core/group-person';
import { Group } from '../../../../models/data/app-core/group';
import { MatTableDataSource } from '@angular/material/table';
import { OrganizationalUnit } from '../../../../models/data/app-core/organizational-unit';
import { OrganizationalUnitPerson } from '../../../../models/data/app-core/organizational-unit-person';

@Component({
    selector: 'app-person',
    templateUrl: './person.component.html',
    styleUrls: ['./person.component.css', '../../../../app.component.css']
})

export class PersonComponent extends EditorComponent {

    public avatarUrl: string = "";
    public availableOrganizationalUnits: AvatarPrimitive[] = [];
    public selectedOrganizationalUnitIds: number[] = [];
    public availableGroups: AvatarPrimitive[] = [];
    public selectedGroupIds: number[] = [];
    public dynamicClassNamePersonSpan = "expanding-span-collapsed";
    public personEnvelope: PersonEnvelope | null = null;

    @ViewChild("appHistory") appHistory!: HistoryComponent;
    @ViewChild("groupsChipSelector") groupsChipSelector!: ChipSelectorComponent;
    @ViewChild("organizationalUnitChipSelector") organizationalUnitChipSelector!: ChipSelectorComponent;

    public constructor(public override appService: AppService,
                       public override itemService: PersonService,
                       public override route: ActivatedRoute,
                       public override location: Location) {

        super(appService, itemService, route, location, "person", "persons");
    }

    public ngAfterViewInit(): void {

        this.organizationalUnitChipSelector.itemAdded.subscribe((item: AvatarPrimitive) => {

            this.selectedOrganizationalUnitIds.push(item.Id);
            this.addOrganizationalUnitToPerson(item.Id);
        });

        this.organizationalUnitChipSelector.itemRemoved.subscribe((item: AvatarPrimitive) => {

            this.selectedOrganizationalUnitIds.splice(this.selectedOrganizationalUnitIds.indexOf(item.Id), 1);
            this.removeOrganizationalUnitFromPerson(item.Id);
        });

        this.groupsChipSelector.itemAdded.subscribe((item: AvatarPrimitive) => {

            this.selectedGroupIds.push(item.Id);
            this.addGroupToPerson(item.Id);
        });

        this.groupsChipSelector.itemRemoved.subscribe((item: AvatarPrimitive) => {

            this.selectedGroupIds.splice(this.selectedGroupIds.indexOf(item.Id), 1);
            this.removeGroupFromPerson(item.Id);
        });            
    }

    public override initializeEnvelope = (envelope: PersonEnvelope | null): void => {

        super.initializeEnvelope(envelope);

        this.itemEnvelope = new PersonEnvelope(envelope);
        this.itemEnvelopeSnapshot = this.appService.cloneObject(this.itemEnvelope);
        this.personEnvelope = this.itemEnvelope as PersonEnvelope;

        this.avatarUrl = envelope?.Document.Item?.AvatarBlobPath == null ? this.appService.defaultImageUri : envelope.Document.Item.AvatarBlobPath;

        const availableGroups: AvatarPrimitive[] = (this.itemEnvelope as PersonEnvelope).GroupsPool.map((i: Group) => {
            let result = new AvatarPrimitive();
            result.Id = i.GroupId;
            result.Name = i.Name;
            result.AvatarBlobPath = this.appService.defaultImageUri;
            return result;
        });

        const availableOrganizationalUnits: AvatarPrimitive[] = (this.itemEnvelope as PersonEnvelope).OrganizationalUnitsPool.map((i: OrganizationalUnit) => {
            let result = new AvatarPrimitive();
            result.Id = i.OrganizationalUnitId;
            result.Name = i.Name;
            result.AvatarBlobPath = i.AvatarBlobPath ?? this.appService.defaultImageUri;
            return result;
        });

        this.availableOrganizationalUnits = availableOrganizationalUnits;
        this.availableGroups = availableGroups;

        this.selectedOrganizationalUnitIds = (this.itemEnvelope?.Document as PersonDocument).OrganizationalUnits.map(i => i.OrganizationalUnitId);
        this.selectedGroupIds = (this.itemEnvelope?.Document as PersonDocument).Groups.map(i => i.GroupId);
    }

    public override handleEditMode = (): void => {

        this.appService.navigateTo("person/" + this.itemId.toString(), "");
    }
    
    public addOrganizationalUnitToPerson = (organizationalUnitId: number): void => {

        const document: PersonDocument = (this.itemEnvelope?.Document as PersonDocument);
        const newOrganizationalUnitPerson: OrganizationalUnitPerson = new OrganizationalUnitPerson();

        newOrganizationalUnitPerson.PersonId = document.Item.PersonId;
        newOrganizationalUnitPerson.OrganizationalUnitId = organizationalUnitId;

        document.OrganizationalUnits.push(newOrganizationalUnitPerson);
    }

    public removeOrganizationalUnitFromPerson = (organizationalUnitId: number): void => {

        const document: PersonDocument = (this.itemEnvelope?.Document as PersonDocument);

        const itemIndex: number | undefined = document.OrganizationalUnits.map((i: OrganizationalUnitPerson) => i.OrganizationalUnitId).indexOf(organizationalUnitId, 0);

        if (itemIndex != undefined && itemIndex > -1) {

            document.OrganizationalUnits.splice(itemIndex, 1);
        }
    }

    public addGroupToPerson = (groupId: number): void => {

        const document: PersonDocument = (this.itemEnvelope?.Document as PersonDocument);
        const newGroupPerson: GroupPerson = new GroupPerson();

        newGroupPerson.PersonId = document.Item.PersonId;
        newGroupPerson.GroupId = groupId;

        document.Groups.push(newGroupPerson);
    }

    public removeGroupFromPerson = (groupId: number): void => {

        const document: PersonDocument = (this.itemEnvelope?.Document as PersonDocument);

        const itemIndex: number | undefined = document.Groups.map((i: GroupPerson) => i.GroupId).indexOf(groupId, 0);

        if (itemIndex != undefined && itemIndex > -1) {

            document.Groups.splice(itemIndex, 1);
        }
    }

    public onTabChange(event: MatTabChangeEvent) {

        if (event.index == 1 && !this.isRevisionListLoaded) {

            this.appHistory.searchRevisions();
        }
    }

    public editAvatar = (): void => {

        this.appService.showPendingFeatureDialog();
    }
}
